/**
 * validationRules.js
 * Centralized validation utility with Smart Range warnings for agricultural metrics.
 * Supports bilingual (EN/AR) inline error messages and field-specific range limits.
 */

// ── Smart Range Definitions ──────────────────────────────────────────────────
// Each entry: { min, max, warnLow, warnHigh, unit, warnMsgEn, warnMsgAr }
export const SMART_RANGES = {
  pH: {
    min: 0, max: 14, warnLow: 3, warnHigh: 10, unit: '',
    warnMsgEn: 'Extreme pH — verify your measurement instrument.',
    warnMsgAr: 'حموضة شديدة — تحقق من جهاز القياس.',
    errorMsgEn: 'pH must be between 0 and 14.',
    errorMsgAr: 'يجب أن يكون الرقم الهيدروجيني بين 0 و 14.',
  },
  temperature: {
    min: -10, max: 60, warnLow: 0, warnHigh: 50, unit: '°C',
    warnMsgEn: 'Unusually extreme temperature for agriculture.',
    warnMsgAr: 'درجة حرارة غير عادية للزراعة.',
    errorMsgEn: 'Temperature must be between -10°C and 60°C.',
    errorMsgAr: 'يجب أن تكون درجة الحرارة بين -10 و 60 درجة مئوية.',
  },
  humidity: {
    min: 0, max: 100, warnLow: 5, warnHigh: 98, unit: '%',
    warnMsgEn: 'Humidity reading seems unusual — please verify.',
    warnMsgAr: 'قراءة الرطوبة غير عادية — يرجى التحقق.',
    errorMsgEn: 'Humidity must be between 0% and 100%.',
    errorMsgAr: 'يجب أن تكون الرطوبة بين 0% و 100%.',
  },
  nitrogen: {
    min: 0, max: 800, warnLow: null, warnHigh: 600, unit: 'mg/kg',
    warnMsgEn: 'Very high nitrogen — confirm lab results.',
    warnMsgAr: 'نيتروجين مرتفع جداً — تحقق من نتائج المختبر.',
    errorMsgEn: 'Nitrogen must be between 0 and 800 mg/kg.',
    errorMsgAr: 'يجب أن يكون النيتروجين بين 0 و 800.',
  },
  phosphorus: {
    min: 0, max: 800, warnLow: null, warnHigh: 600, unit: 'mg/kg',
    warnMsgEn: 'Very high phosphorus — confirm lab results.',
    warnMsgAr: 'فسفور مرتفع جداً — تحقق من نتائج المختبر.',
    errorMsgEn: 'Phosphorus must be between 0 and 800 mg/kg.',
    errorMsgAr: 'يجب أن يكون الفسفور بين 0 و 800.',
  },
  potassium: {
    min: 0, max: 800, warnLow: null, warnHigh: 600, unit: 'mg/kg',
    warnMsgEn: 'Very high potassium — confirm lab results.',
    warnMsgAr: 'بوتاسيوم مرتفع جداً — تحقق من نتائج المختبر.',
    errorMsgEn: 'Potassium must be between 0 and 800 mg/kg.',
    errorMsgAr: 'يجب أن يكون البوتاسيوم بين 0 و 800.',
  },
  soilMoisture: {
    min: 0, max: 100, warnLow: 5, warnHigh: 95, unit: '%',
    warnMsgEn: 'Soil moisture seems unusual — verify sensor.',
    warnMsgAr: 'رطوبة التربة غير عادية — تحقق من المستشعر.',
    errorMsgEn: 'Soil moisture must be between 0% and 100%.',
    errorMsgAr: 'يجب أن تكون رطوبة التربة بين 0% و 100%.',
  },
  ec: {
    min: 0, max: 20, warnLow: null, warnHigh: 10, unit: 'dS/m',
    warnMsgEn: 'Very high EC — potential salinity issue.',
    warnMsgAr: 'موصلية كهربائية عالية جداً — مشكلة ملوحة محتملة.',
    errorMsgEn: 'EC must be between 0 and 20 dS/m.',
    errorMsgAr: 'يجب أن تكون الموصلية الكهربائية بين 0 و 20.',
  },
  rainfall: {
    min: 0, max: 5000, warnLow: null, warnHigh: 3000, unit: 'mm',
    warnMsgEn: 'Very high rainfall value — please verify.',
    warnMsgAr: 'كمية أمطار مرتفعة جداً — يرجى التحقق.',
    errorMsgEn: 'Rainfall must be between 0 and 5000 mm.',
    errorMsgAr: 'يجب أن تكون كمية الأمطار بين 0 و 5000 ملم.',
  },
  duration: {
    min: 1, max: 1440, warnLow: null, warnHigh: 720, unit: 'min',
    warnMsgEn: 'Duration exceeds 12 hours — is this intentional?',
    warnMsgAr: 'المدة تتجاوز 12 ساعة — هل هذا مقصود؟',
    errorMsgEn: 'Duration must be between 1 and 1440 minutes.',
    errorMsgAr: 'يجب أن تكون المدة بين 1 و 1440 دقيقة.',
  },
  mobile: {
    min: 7, max: 15, // character length
    errorMsgEn: 'Mobile number must be 7-15 digits.',
    errorMsgAr: 'يجب أن يكون رقم الهاتف من 7 إلى 15 رقمًا.',
  },
};

// Mapping of common field keys to their smart range type
const FIELD_TO_RANGE = {
  N: 'nitrogen', P: 'phosphorus', K: 'potassium',
  temp: 'temperature', Temperature: 'temperature',
  hum: 'humidity', Humidity: 'humidity',
  ph: 'pH', pH: 'pH',
  Rainfall: 'rainfall', rainfall: 'rainfall',
  moisture: 'soilMoisture', Soil_Moisture: 'soilMoisture',
  ec: 'ec', Electrochemical_Signal: 'ec',
  ambient_temp: 'temperature', Ambient_Temperature: 'temperature',
  soil_temp: 'temperature', Soil_Temperature: 'temperature',
  nitrogen: 'nitrogen', Nitrogen_Level: 'nitrogen',
  phosphorus: 'phosphorus', Phosphorus_Level: 'phosphorus',
  potassium: 'potassium', Potassium_Level: 'potassium',
  duration_minutes: 'duration',
};

/**
 * Strip all non-numeric characters (except minus sign and decimal point).
 * Returns { valid: boolean, sanitized: string }
 */
export function sanitizeNumeric(value) {
  if (value === '' || value === null || value === undefined) {
    return { valid: true, sanitized: '' };
  }
  const str = String(value);
  // Allow optional leading minus and decimal point
  const sanitized = str.replace(/[^0-9.\-]/g, '');
  // Check if it's a valid number
  const num = Number(sanitized);
  return {
    valid: sanitized !== '' && !isNaN(num),
    sanitized,
  };
}

/**
 * Strip non-digit characters for mobile numbers.
 */
export function sanitizeMobile(value) {
  if (!value) return '';
  return String(value).replace(/[^0-9+\-\s()]/g, '');
}

/**
 * Validate a numeric field against its smart range.
 * @param {string} fieldKey - The field identifier (e.g., 'N', 'pH', 'temp')
 * @param {number|string} value - The current value
 * @param {string} lang - 'en' or 'ar'
 * @returns {{ status: 'valid'|'warning'|'error', message: string }}
 */
export function validateField(fieldKey, value, lang = 'en') {
  const isAr = lang === 'ar';
  const rangeKey = FIELD_TO_RANGE[fieldKey];
  if (!rangeKey) return { status: 'valid', message: '' };

  const range = SMART_RANGES[rangeKey];
  if (!range) return { status: 'valid', message: '' };

  // Empty is not an error (optional fields)
  if (value === '' || value === null || value === undefined) {
    return { status: 'valid', message: '' };
  }

  const num = Number(value);
  if (isNaN(num)) {
    return {
      status: 'error',
      message: isAr ? 'يجب إدخال رقم صالح.' : 'Please enter a valid number.',
    };
  }

  // Range check
  if (num < range.min || num > range.max) {
    return {
      status: 'error',
      message: isAr ? range.errorMsgAr : range.errorMsgEn,
    };
  }

  // Warning check
  if (range.warnLow !== null && range.warnLow !== undefined && num < range.warnLow) {
    return { status: 'warning', message: isAr ? range.warnMsgAr : range.warnMsgEn };
  }
  if (range.warnHigh !== null && range.warnHigh !== undefined && num > range.warnHigh) {
    return { status: 'warning', message: isAr ? range.warnMsgAr : range.warnMsgEn };
  }

  return { status: 'valid', message: '' };
}

/**
 * Validate a mobile number.
 */
export function validateMobile(value, lang = 'en') {
  const isAr = lang === 'ar';
  if (!value) return { status: 'valid', message: '' };
  const digitsOnly = String(value).replace(/[^0-9]/g, '');
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return {
      status: 'error',
      message: isAr ? SMART_RANGES.mobile.errorMsgAr : SMART_RANGES.mobile.errorMsgEn,
    };
  }
  // Check no letters
  if (/[a-zA-Zا-ي]/.test(value)) {
    return {
      status: 'error',
      message: isAr ? 'رقم الهاتف يجب أن يحتوي على أرقام فقط.' : 'Phone number must contain digits only.',
    };
  }
  return { status: 'valid', message: '' };
}

/**
 * Check if all fields in a form are valid.
 * @param {Object} formData - key:value pairs
 * @param {string[]} fieldKeys - keys to validate
 * @param {string} lang - 'en' or 'ar'
 * @returns {boolean}
 */
export function isFormValid(formData, fieldKeys, lang = 'en') {
  return fieldKeys.every(key => {
    const result = validateField(key, formData[key], lang);
    return result.status !== 'error';
  });
}

/**
 * Compute age from a date of birth string (ISO format).
 */
export function computeAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}
